
import { LinkTypeEnum, ParserFieldObject } from '@/types';
import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch,
} from 'vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import WhatsappCtasModal from '@/components/template-builder/modals/WhatsappCtasModal.vue';
import { useI18n } from 'vue-i18n';
import { isWhatsappSmartListEnabled } from '../getters/whatsapp-smart-product-list';

interface CtaData {
  translationId: string | null;
  value: string;
  action: {
    type: string;
    value: string;
  };
}

export default defineComponent({
  name: 'WhatsappCtas',

  components: {
    SpmButton,
    WhatsappCtasModal,
  },

  props: {
    configs: {
      type: Object,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, { emit }) {
    const { t } = useI18n();
    const ctasData = ref<CtaData[]>([]);
    const isModalVisible = ref(false);

    // Count buttons by type
    const buttonCounts = computed(() => {
      const counts = {
        callNumber: 0,
        webAddress: 0,
        total: ctasData.value.length,
      };

      ctasData.value.forEach((cta) => {
        if (cta.action.type === LinkTypeEnum.CALL_NUMBER) {
          counts.callNumber += 1;
        } else if (cta.action.type === LinkTypeEnum.WEB_ADDRESS) {
          counts.webAddress += 1;
        }
      });

      return counts;
    });

    // Generate appropriate limit message
    const limitReachedMessage = computed(() => {
      if (isWhatsappSmartListEnabled() && buttonCounts.value.total >= 1) {
        return t('templateBuilder.whatsapp.maxButtonsSmartlist');
      }
      if (buttonCounts.value.total >= 3) {
        return t('templateBuilder.whatsapp.maxButtonsReached');
      }
      if (buttonCounts.value.callNumber >= 1 && buttonCounts.value.webAddress >= 2) {
        return t('templateBuilder.whatsapp.maxButtonTypesReached');
      }
      return null;
    });

    // Watch for changes in parser values and update local state
    watch(
      () => props.parserValues.properties[0].value,
      (newValue) => {
        if (Array.isArray(newValue)) {
          ctasData.value = JSON.parse(JSON.stringify(newValue));
        }
      },
      { immediate: true, deep: true },
    );

    const openModal = () => {
      isModalVisible.value = true;
    };

    const handleModalChange = (payload: any) => {
      emit('on-change-properties', payload);
    };

    return {
      t,
      ctasData,
      isModalVisible,
      limitReachedMessage,
      openModal,
      handleModalChange,
    };
  },
});
